// LIBRARIES
import React from 'react';
import jstz from 'jstz';
import { Formik, Field } from 'formik';
import { InputNumber } from 'antd';
import styled from 'styled-components';
import values from 'lodash/values';

// HELPERS
import { toFriendlyDateTimeString, preventUserFromTyping } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import TextField from 'helpers/reduxFormMaterialUiNext/TextField';
import { getCurrentLocale } from 'helpers/i18n';
import { padDetailsTypes } from 'helpers/constants';
import * as validator from 'helpers/Validator';

// COMPONENTS
import Header from 'components/shared/Header';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import Form from 'components/ui/Form';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';

const styles = {
  fieldsRow: {
    marginTop: '15px',
    marginBottom: '15px',
  },
};

const FullWidthInput = styled(InputNumber)`
  width: 100%;
`;

const StyleRentPaymentNotice = styled.div`
  margin-left: 10px;
  line-height: 14px;
  font-size: 12px;
`;

const EFTDetails = ({ institution_number, transit_number, account_number }) => (
  <div className="row" style={styles.fieldsRow}>
    <div className="col-sm-4">
      <Field
        name="institution_number"
        component={TextField}
        label={getTranslatedString('autopay.institutionNumber')}
        validate={[validator.institutionNumber]}
        value={institution_number || ''}
        disabled
      />
    </div>
    <div className="col-sm-4">
      <Field
        name="transit_number"
        component={TextField}
        label={getTranslatedString('autopay.transitBranchNumber')}
        validate={[validator.transitNumber]}
        value={transit_number || ''}
        disabled
      />
    </div>
    <div className="col-sm-4">
      <Field
        name="account_number"
        component={TextField}
        label={getTranslatedString('autopay.accountNumber')}
        validate={[validator.accountNumber]}
        value={account_number || ''}
        disabled
      />
    </div>
  </div>
);

const ACHDetails = ({ routing_number, account_number }) => (
  <div className="row" style={styles.fieldsRow}>
    <div className="col-sm-4">
      <Field
        name="routing_number"
        component={TextField}
        label={getTranslatedString('autopay.routingNumber')}
        value={routing_number || ''}
        disabled
      />
    </div>
    <div className="col-sm-4">
      <Field
        name="account_number"
        component={TextField}
        label={getTranslatedString('autopay.accountNumber')}
        value={account_number || ''}
        disabled
      />
    </div>
    <div className="col-sm-4">
      <Field
        name="ach_account_type"
        component={TextField}
        label={getTranslatedString('autopay.accountType')}
        value={values.ach_account_type || ''}
        disabled
      />
    </div>
  </div>
);

const toggleTotalMonthBalanceStatus = (e, setFieldValue, isSaveDisable) => {
  setFieldValue('pay_total_monthly_balance', e.target.checked);
  setFieldValue('authorized_amount', null);
  isSaveDisable(null, e.target.checked);
};

const PadDetailForm = ({ formName, formData, handleSubmit, isSaveDisable }) => {
  const initialValues = {
    id: formData?.id || null,
    pay_total_monthly_balance: formData?.pay_total_amount || false,
    authorized_amount: formData?.authorized_amount || null,
    institution_number: formData?.institution_number || null,
    account_number: formData?.account_number || null,
    transit_number: formData?.transit_number || null,
    ach_account_type: formData?.ach_account_type || null,
    routing_number: formData?.routing_number || null,
    created_at: formData?.created_at
      ? toFriendlyDateTimeString(formData?.created_at, {
          language: getCurrentLocale(),
          timezone: jstz.determine().name(),
        })
      : null,
    updated_at: formData?.updated_at
      ? toFriendlyDateTimeString(formData?.updated_at, {
          language: getCurrentLocale(),
          timezone: jstz.determine().name(),
        })
      : null,
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ handleSubmit, values, setFieldValue }) => (
        <VerticalFormWrapper id={formName} onSubmit={handleSubmit}>
          <Header name="PRE-AUTHORIZED PAYMENT" />
          {formData?.payment_type === padDetailsTypes.EFT ? (
            <EFTDetails {...values} />
          ) : (
            <ACHDetails {...values} />
          )}

          <RenderIfToggleOn toggle="splitpay">
            <CheckboxNormal
              name="pay_total_monthly_balance"
              value={values.pay_total_monthly_balance}
              checked={values.pay_total_monthly_balance}
              onChange={(e) => toggleTotalMonthBalanceStatus(e, setFieldValue, isSaveDisable)}
              style={{ marginTop: '15px', marginLeft: '10px' }}
            >
              {getTranslatedString('landlordPortal.properties.autoPay.payTotalMonthlyBalance')}
            </CheckboxNormal>

            <StyleRentPaymentNotice>
              {getTranslatedString('landlordPortal.properties.rentPayment.notice')}
            </StyleRentPaymentNotice>

            <div style={{ marginTop: '10px', padding: '15px' }}>
              <Form.Item
                label={getTranslatedString(
                  'landlordPortal.properties.autoPay.preAuthorizedAmountLabel'
                )}
              >
                <FullWidthInput
                  size="large"
                  value={values.authorized_amount}
                  min={0}
                  disabled={values.pay_total_monthly_balance}
                  onKeyDown={(e) => preventUserFromTyping(e, '^[0-9¾\b\t]+$')}
                  onChange={(value) => {
                    if (!Number(value) && value?.length > 0) return;
                    setFieldValue('authorized_amount', value);
                    isSaveDisable(value, false);
                  }}
                  precision={2}
                  formatter={(value) => validator.normalizeMoney(value)}
                  name="authorized_amount"
                />
              </Form.Item>
            </div>
          </RenderIfToggleOn>

          <div className="row" style={styles.fieldsRow}>
            <div className="col-xs-12">
              <span style={{ fontFamily: 'Circular-Bold' }}>
                {getTranslatedString('landlordPortal.papForm.uploaded')} {values.created_at}
              </span>
            </div>
            <div className="col-xs-12">
              <span style={{ fontFamily: 'Circular-Bold' }}>
                {getTranslatedString('landlordPortal.papForm.lastUpdated')} {values.updated_at}
              </span>
            </div>
          </div>
        </VerticalFormWrapper>
      )}
    </Formik>
  );
};

export default PadDetailForm;
